<template>
  <q-card>
    <q-form @submit.prevent="updateProject">
      <q-card-section>
        <div class="text-h6">Редактировать проект</div>
      </q-card-section>

      <q-card-section class="text-center">
        <avatar :project="project" size="100px" @click="avatarDialogue = true"/>
        <q-dialog v-model="avatarDialogue">
          <update-project-avatar-form @cancel="avatarDialogue = false" @add="addAvatar"/>
        </q-dialog>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-input dense v-model="newProject.name" label="Имя проекта" :rules="[requiredRule, objectNameRule]"/>
        <q-input dense v-model="newProject.code" label="Код проекта" :rules="[requiredRule, codeRule]"/>
        <q-input dense v-model="newProject.description" type="textarea" label="Описание"/>
        <q-select dense v-model="newProject.organizationId" clearable :loading="isFetchingOrganizations"
                  :options="organizations" option-label="name" option-value="id" label="Организация" emit-value
                  behavior="menu"
                  map-options>
          <template v-slot:no-option>
            <q-item>
              <q-item-section class="text-italic text-grey">
                Ничего не найдено
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <select-project-type
            :initial-value="project.projectType"
            v-if="((user.email == project?.owner?.email) && !project.organization) || (!!project?.organization && permissions?.find(p => p.user.email === user.email)?.permission == 'ADMINISTRATOR')"
            v-model="newProject.typeId"/>

      </q-card-section>

      <q-card-actions align="right" class="text-primary">
        <q-btn no-caps flat label="Удалить" @click="deleteProject" color="negative"/>
        <q-btn no-caps flat label="Обновить" type="submit"/>
      </q-card-actions>
    </q-form>
  </q-card>
</template>

<script setup lang="ts">
import {useQuasar} from "quasar";
import {computed, reactive, ref, watch} from "vue";
import {UpdateProject} from "../models/project";
import {useUpdateProject, useDeleteProject, useProjectProjectTypes} from "../composables/project";
import {useProjectStore} from "../store/project";
import {storeToRefs} from "pinia";
import {useRoute, useRouter} from "vue-router";
import UpdateProjectAvatarForm from "./UpdateProjectAvatarForm.vue";
import Avatar from "./Avatar.vue";
import {useOrganizationUsers, useOwnedOrganization, useProjectUserOrganizations} from "../composables/organization";
import {codeRule, objectNameRule, requiredRule} from "../util/rules";
import {useUserStore} from "../store/user";
import {useRouteParams} from "@vueuse/router";
import SelectProjectType from "./SelectProjectType.vue";


const $q = useQuasar()

const store = useProjectStore()

const {project, loading} = storeToRefs(store)

const userStore = useUserStore()
const projectId = useRouteParams('projectId', undefined, {transform: Number})

const {user} = storeToRefs(userStore)

const {data: projectTypes, isFetching, execute: fetchProjectTypes} = useProjectProjectTypes(projectId)
const {data: organizations, isFetching: isFetchingOrganizations} = useProjectUserOrganizations(projectId)

const avatarDialogue = ref(false)

const organizationId = computed(() => project.value?.organization?.id)

const route = useRoute()
const router = useRouter()


watch(project, (val) => {
  newProject.name = project.value?.name
  newProject.code = project.value?.code
  newProject.description = project.value?.description
  newProject.typeId = project.value?.projectType?.id || null
  newProject.organizationId = project.value?.organization?.id || null
})

const {data: permissions, execute: fetchUsers} = useOrganizationUsers(organizationId)


const newProject = reactive<UpdateProject>({
  name: project.value?.name,
  code: project.value?.code,
  description: project.value?.description,
  typeId: project.value?.projectType?.id || null,
  organizationId: project.value?.organization?.id || null,
})

const {execute} = useUpdateProject(projectId, newProject)

const {execute: executeDelete} = useDeleteProject(projectId)

async function updateProject() {
  await execute(true)
  await store.select(projectId.value)
  await fetchProjectTypes(true)
}

async function deleteProject() {
  $q.dialog({
    title: 'Удаление',
    message: 'Для удаления введите имя проекта',
    prompt: {
      model: '',
      isValid: val => val === project.value?.name, // << here is the magic
      type: 'text' // optional
    },
    cancel: true,
    persistent: true
  }).onOk(async data => {
    if (data === project.value?.name) {
      await executeDelete()
      await router.push('/projects')
    }

  })

}

async function addAvatar() {
  await store.select(projectId.value)
  avatarDialogue.value = false
}
</script>

<style scoped></style>